import { get, isEmpty, uniqBy } from 'lodash';
import { Schema, Metric, Dimension, Rollup } from './schema.model';
import { Query } from './query.model';

export class SliceProperty {
  constructor(readonly name: string, readonly value: string, readonly meta: any = null) {}

  get format() {
    return get(this.meta, 'display.format')
  }
}

export class Slice {
  constructor(
    readonly dimension: Dimension,
    private readonly _value: string,
    readonly data: any = null,
    readonly properties: SliceProperty[] = null,
    readonly meta: any = null
  ) {}

  get id() {
    return this._value
  }

  get value() {
    const value = <string> get(this.data, this.dimension.name) || this._value
    return value && value.length ? value : 'n/a'
  }

  get format() {
    return get(this.meta, 'display.format')
  }
}

export class Series {
  constructor(readonly slices: Slice[], readonly metrics: MetricValues[]) {}
}

export class Row {
  constructor(readonly date: Date, readonly slices: Slice[], readonly metrics: MetricValue[]) {}
}

export class MetricCommon {
  readonly name: string

  constructor(private readonly metric: Metric) {
    this.name = metric.name
  }

  get displayName() {
    return this.metric.displayName
  }

  get format() {
    return this.metric.format
  }
}

export class MetricValues extends MetricCommon {
  constructor(metric: Metric, readonly values: number[], readonly aggregate: number) {
    super(metric)
  }
}

export class MetricValue extends MetricCommon {
  constructor(metric: Metric, readonly value: number) {
    super(metric)
  }
}

export interface QueryWarning {
  type: string
  metric: string
  rows: number
}

export class QueryResult {
  readonly schema: Schema
  readonly timestamps: number[]
  readonly dimensions: Dimension[]
  readonly metrics: Metric[]
  readonly totals: Record<string, MetricValue>
  readonly rollup: Rollup
  readonly series: Series[]
  readonly rows: Row[]
  readonly nextSlice: any
  readonly limit: number
  readonly warnings: QueryWarning[];

  private _dimensionProperties = {}

  TOTAL = 'total'

  constructor(readonly query: Query, private rawQueryResult) {
    this.schema = query.schema
    this.timestamps = rawQueryResult['timestamps'].map(ts => ts * 1000)
    this.dimensions = this.parseDimensions(rawQueryResult['cube'])

    for (const dimension of this.dimensions) {
      const propertyNames = query.dimensionProperties[dimension.name]
      if (propertyNames) {
        this._dimensionProperties[dimension.name] = {}
        for (const propertyName of propertyNames) {
          this._dimensionProperties[dimension.name][propertyName] = dimension.properties.find(p => p.name === propertyName)
        }
      }
    }

    this.metrics = this.parseMetrics(rawQueryResult['metrics'])
    this.rollup = new Rollup(rawQueryResult['rollup'])
    this.limit = rawQueryResult['limit']
    this.nextSlice = rawQueryResult['next_slice']
    this.warnings = rawQueryResult['warnings'] ?? [];

    const rawSeries = rawQueryResult['series']
    this.series = this.parseSeries(rawSeries)
    this.rows = this.parseRows(rawSeries)
    this.totals = this.parseTotals(rawQueryResult['totals'])
  }

  getSlices(dimension: Dimension) {
    const index = this.dimensions.findIndex(d => d.name === dimension.name)
    return uniqBy(this.rows.map(row => row.slices[index]), s => s.value)
  }

  private parseSeries(rawSeries) {
    return rawSeries.map(raw => {
      const slices = this.parseSlices(raw)
      const metrics = this.parseSeriesMetric(raw['metrics'])
      return new Series(slices, metrics)
    })
  }

  private parseRows(rawSeries) {
    return rawSeries.map(raw => {
      const slices = this.parseSlices(raw)
      return this.timestamps.map((timestamp, index) => {
        const date = new Date(timestamp);
        const metrics = this.parseRowMetrics(raw['metrics'], index)
        return new Row(date, slices, metrics)
      })
    }).flat()
  }

  private parseDimensions(rawDimensions) {
    if (rawDimensions.length == 0) {
      return [new Dimension('total')]
    }

    return rawDimensions.map(dimensionName => {
      const schemaDimension = this.schema.fetchDimension(dimensionName)

      const props = {}
      for (const prop of schemaDimension.properties) {
        props[prop.name] = prop
      }

      let dimensionProperties
      const queryDimensionProperties = this.query.dimensionProperties[dimensionName]
      if (queryDimensionProperties) {
        dimensionProperties = queryDimensionProperties.map(dp => props[dp]).filter(Boolean)
      }
      return new Dimension(dimensionName, schemaDimension.meta, dimensionProperties)
    })
  }

  private parseSlices(rawRow) {
    const rawSlice = rawRow['slice']
    if (isEmpty(rawSlice)) {
      return [new Slice(this.dimensions[0], 'Total')]
    }

    const rawData = rawRow['data'] || {}
    return this.dimensions.map(dimension => {
      const dimName = dimension.name
      const value = rawSlice[dimName]
      const data = rawData[dimName]
      const meta = dimension.meta

      const properties = this.query.dimensionProperties[dimName] || []
      const sliceProperties = properties.map(property => {
        const dimensionProperty = this._dimensionProperties[dimName][property]
        if (dimensionProperty) {
          const propMeta = dimensionProperty.meta
          if (property === 'id') {
            return new SliceProperty(property, value, propMeta)
          } else {
            return new SliceProperty(property, <string>get(data, property) || null, propMeta)
          }
        }
      })

      return new Slice(dimension, value, data, sliceProperties, meta)
    })
  }

  private parseSeriesMetric(rawMetrics) {
    return rawMetrics.map(rawMetric => {
      const metric = this.schema.fetchMetric(rawMetric['name'])
      const values = rawMetric['values']
      const aggregate = rawMetric['aggregate']
      return new MetricValues(metric, values, aggregate)
    })
  }

  private parseRowMetrics(rawMetrics, index) {
    return rawMetrics.map(rawMetric => {
      const metric = this.schema.fetchMetric(rawMetric['name'])
      const value = rawMetric['values'][index]
      return new MetricValue(metric, value)
    })
  }

  private parseMetrics(rawMetrics) {
    return rawMetrics.map(metricName => this.schema.fetchMetric(metricName))
  }

  private parseTotals(rawTotals: Record<string, number>) {
    if (!rawTotals) {
      return [];
    }

    return Object.entries(rawTotals).reduce((acc, [metricName, total]) => {
      const metric = this.schema.fetchMetric(metricName);
      if (metric) {
        acc[metricName] = new MetricValue(metric, total);
      }
      return acc;
    }, {});
  }

  isLimited() {
    return !!this.nextSlice
  }
}
